import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    Divider,
    Alert,
    IconButton,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';

// Contexts and Components
import { PERMISSION_TYPES, RESOURCE_TYPES } from '../../contexts/PermissionContext';

// Services
import { permissionsAPI } from '../../services/api';

const PermissionRow = ({
    id,
    name,
    subtitle,
    directPerm,
    effective,
    onPermissionChange,
}) => (
    <TableRow hover>
        <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
                <Box>
                    <Typography variant="body2" fontWeight={500}>{name}</Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                    )}
                </Box>
            </Box>
        </TableCell>
        <TableCell align="center">
            <ToggleButtonGroup
                value={directPerm}
                exclusive
                onChange={(event, value) => onPermissionChange(id, value)}
                size="small"
            >
                <ToggleButton
                    value={PERMISSION_TYPES.ALLOW}
                    sx={{
                        px: 1.5,
                        '&.Mui-selected': {
                            bgcolor: 'success.light',
                            color: 'success.dark',
                            '&:hover': { bgcolor: 'success.light' },
                        },
                    }}
                >
                    <CheckIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Allow
                </ToggleButton>
                <ToggleButton
                    value={PERMISSION_TYPES.DENY}
                    sx={{
                        px: 1.5,
                        '&.Mui-selected': {
                            bgcolor: 'error.light',
                            color: 'error.dark',
                            '&:hover': { bgcolor: 'error.light' },
                        },
                    }}
                >
                    <BlockIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Deny
                </ToggleButton>
            </ToggleButtonGroup>
        </TableCell>
        <TableCell align="center">
            {directPerm && (
                <Tooltip title="Clear permission">
                    <IconButton size="small" onClick={() => onPermissionChange(id, null)}>
                        <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
        </TableCell>
        <TableCell align="center">
            {effective.permission === PERMISSION_TYPES.ALLOW && (
                <Chip
                    icon={<CheckIcon sx={{ fontSize: 14 }} />}
                    label={effective.inherited ? 'Inherited' : 'Allowed'}
                    size="small"
                    color="success"
                    variant={effective.inherited ? 'outlined' : 'filled'}
                />
            )}
            {effective.permission === PERMISSION_TYPES.DENY && (
                <Chip
                    icon={<BlockIcon sx={{ fontSize: 14 }} />}
                    label={effective.inherited ? 'Blocked' : 'Denied'}
                    size="small"
                    color="error"
                    variant={effective.inherited ? 'outlined' : 'filled'}
                />
            )}
            {!effective.permission && (
                <Chip label="No Access" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
            )}
        </TableCell>
    </TableRow>
);

const PermissionDialog = ({
    open,
    onClose,
    resourceType,
    resourceId,
    resourceName,
    parentInfo = null,
    companyId,
    assignees = [],
}) => {
    const [localPermissions, setLocalPermissions] = useState({});
    const [originalDirectPermissions, setOriginalDirectPermissions] = useState({});
    const [subjectAssignments, setSubjectAssignments] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const getParentChain = (info) => {
        if (!info || !info.type || !info.id) {
            return [];
        }

        return [
            { resourceType: info.type, resourceId: info.id },
            ...getParentChain(info.parentInfo || null),
        ];
    };

    const parentChain = getParentChain(parentInfo);

    const getAssignmentsForUser = (userId) => subjectAssignments[userId] || [];

    const getDirectFromAssignments = (userId, type, id) => {
        const assignment = getAssignmentsForUser(userId).find(
            (item) => item.resourceType === type && item.resourceId === id
        );
        return assignment ? assignment.permission : null;
    };

    useEffect(() => {
        if (!open || !companyId || !resourceType || !resourceId) {
            return;
        }

        let isMounted = true;

        const loadPermissions = async () => {
            setIsLoading(true);
            try {
                const results = await Promise.all(
                    assignees.map(async (assignee) => {
                        const response = await permissionsAPI.listBySubject({
                            companyId,
                            subjectType: 'user',
                            subjectId: assignee.id,
                        });

                        const normalized = Array.isArray(response?.items)
                            ? response.items.map((item) => ({
                                resourceType: item.resourceType || item.resource_type,
                                resourceId: item.resourceId || item.resource_id,
                                permission: item.permission,
                            }))
                            : [];

                        return [assignee.id, normalized];
                    })
                );

                if (!isMounted) {
                    return;
                }

                const nextAssignments = Object.fromEntries(results);
                const nextDirectPermissions = {};

                assignees.forEach((assignee) => {
                    const userAssignments = nextAssignments[assignee.id] || [];
                    const direct = userAssignments.find(
                        (item) => item.resourceType === resourceType && item.resourceId === resourceId
                    );
                    if (direct?.permission) {
                        nextDirectPermissions[assignee.id] = direct.permission;
                    }
                });

                setSubjectAssignments(nextAssignments);
                setLocalPermissions(nextDirectPermissions);
                setOriginalDirectPermissions(nextDirectPermissions);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadPermissions();

        return () => {
            isMounted = false;
        };
    }, [open, companyId, resourceType, resourceId, assignees]);

    const handlePermissionChange = (assigneeId, newPermission) => {
        setLocalPermissions((prev) => {
            const updated = { ...prev };
            if (newPermission === null) {
                delete updated[assigneeId];
            } else {
                updated[assigneeId] = newPermission;
            }
            return updated;
        });
    };

    const handleSave = async () => {
        if (!companyId) {
            onClose();
            return;
        }

        setIsSaving(true);
        try {
            const changedAssignees = assignees.filter((assignee) => {
                const original = originalDirectPermissions[assignee.id] || null;
                const current = localPermissions[assignee.id] || null;
                return original !== current;
            });

            await Promise.all(
                changedAssignees.map(async (assignee) => {
                    const existingAssignments = getAssignmentsForUser(assignee.id);
                    const assignmentsExcludingCurrent = existingAssignments.filter(
                        (item) => !(item.resourceType === resourceType && item.resourceId === resourceId)
                    );

                    const nextDirectPermission = localPermissions[assignee.id] || null;
                    const nextAssignments = nextDirectPermission
                        ? [
                            ...assignmentsExcludingCurrent,
                            {
                                resourceType,
                                resourceId,
                                permission: nextDirectPermission,
                            },
                        ]
                        : assignmentsExcludingCurrent;

                    await permissionsAPI.bulkReplace({
                        companyId,
                        subjectType: 'user',
                        subjectId: assignee.id,
                        assignments: nextAssignments,
                    });
                })
            );

            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const getResourceTypeLabel = () => {
        switch (resourceType) {
            case RESOURCE_TYPES.PROJECT:
                return 'Project';
            case RESOURCE_TYPES.TASK:
                return 'Task';
            case RESOURCE_TYPES.SUBTASK:
                return 'Subtask';
            default:
                return 'Resource';
        }
    };

    const getEffectiveStatus = (assigneeId) => {
        const directPerm = localPermissions[assigneeId] || null;

        if (directPerm === PERMISSION_TYPES.DENY) {
            return { permission: PERMISSION_TYPES.DENY, inherited: false };
        }
        if (directPerm === PERMISSION_TYPES.ALLOW) {
            return { permission: PERMISSION_TYPES.ALLOW, inherited: false };
        }

        for (const parent of parentChain) {
            const parentPerm = getDirectFromAssignments(assigneeId, parent.resourceType, parent.resourceId);
            if (parentPerm === PERMISSION_TYPES.DENY) {
                return { permission: PERMISSION_TYPES.DENY, inherited: true };
            }
            if (parentPerm === PERMISSION_TYPES.ALLOW) {
                return { permission: PERMISSION_TYPES.ALLOW, inherited: true };
            }
        }

        return { permission: null, inherited: false };
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="inherit" />
                        <Typography variant="h6" color="inherit">Permissions</Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" color="inherit">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        {getResourceTypeLabel()}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {resourceName}
                    </Typography>
                    {parentInfo && (
                        <Typography variant="caption" color="text.secondary">
                            Parent: {parentInfo.name}
                        </Typography>
                    )}
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        <strong>DENY</strong> always takes precedence over ALLOW. Global permissions from parent resources
                        are shown as inherited or blocked.
                    </Typography>
                </Alert>

                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={600}>Project Users</Typography>
                        <Chip label={assignees.length} size="small" variant="outlined" />
                    </Box>

                    {isLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                Loading existing and global permissions...
                            </Typography>
                        </Box>
                    )}

                    <TableContainer sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 200 }} align="center">Permission</TableCell>
                                    <TableCell sx={{ fontWeight: 600, width: 50 }} align="center" />
                                    <TableCell sx={{ fontWeight: 600, width: 150 }} align="center">Effective</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignees.map((assignee) => (
                                    <PermissionRow
                                        key={assignee.id}
                                        id={assignee.id}
                                        name={assignee.name}
                                        subtitle={assignee.subtitle || 'Project member'}
                                        directPerm={localPermissions[assignee.id] || null}
                                        effective={getEffectiveStatus(assignee.id)}
                                        onPermissionChange={handlePermissionChange}
                                    />
                                ))}

                                {!isLoading && assignees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No project users found. Add task owners or project manager to manage access.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSaving}>
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={isLoading || isSaving}>
                    {isSaving ? 'Saving...' : 'Save Permissions'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PermissionDialog;
